// Selects custom (sin dropdown nativo del OS) de país y rubro. Extraídos de
// `Onboarding.tsx` para compartirlos con el alta enterprise (`EnterpriseCheckout`)
// sin duplicar el listado ni el markup. Ver ADR.

import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

export const countries = [
  'Argentina',
  'Bolivia',
  'Brasil',
  'Chile',
  'Colombia',
  'Ecuador',
  'Paraguay',
  'Perú',
  'Uruguay',
  'Venezuela',
  'México',
  'Estados Unidos',
  'España',
  'Otro',
];

export const rubroOptions = [
  'Moda e Indumentaria',
  'Hogar y Decoración',
  'Belleza y Cuidado Personal',
  'Tecnología',
  'Salud y Bienestar',
  'Automotriz',
  'Gastronomía',
  'Juguetería',
  'Arte y Manualidades',
  'Librería',
  'Creación de Contenido',
  'Otro',
];

/* ── Custom Country Select (no native OS dropdown) ── */
export const CountrySelect = ({
  value,
  onChange,
  required,
}: {
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Al abrir, si no hay lugar suficiente abajo, abrimos HACIA ARRIBA — así el
  // dropdown no se pasa del viewport ni estira la página (evita el scroll + el
  // hueco en blanco). Se mide el espacio bajo el campo en ese momento.
  const toggleOpen = () => {
    if (!open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setOpenUp(window.innerHeight - rect.bottom < 260);
    }
    setOpen((o) => !o);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      {required && (
        <input
          tabIndex={-1}
          autoComplete="off"
          style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
          value={value}
          onChange={() => {}}
          required
        />
      )}
      <button
        type="button"
        onClick={toggleOpen}
        className="w-full flex items-center justify-between px-3 py-2.5 border border-neutral-200 rounded-md text-[15px] bg-surface-card hover:bg-surface-elevated focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors"
      >
        <span className={value ? 'text-brand-black/90' : 'text-neutral-400'}>
          {value || 'Selecciona tu país'}
        </span>
        <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className={`absolute z-50 w-full bg-surface-card border border-neutral-200 rounded-md shadow-md py-1 max-h-60 overflow-y-auto ${
            openUp ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
        >
          {countries.map((country) => (
            <button
              key={country}
              type="button"
              onClick={() => {
                onChange(country);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-[15px] transition-colors flex items-center justify-between ${
                value === country ? 'bg-neutral-100 text-brand-black/90 font-medium' : 'text-neutral-600 hover:bg-neutral-100'
              }`}
            >
              <span>{country}</span>
              {value === country && <Check className="w-4 h-4 text-brand-primary shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Custom Rubro Select (no native OS dropdown) ── */
export const RubroSelect = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Al abrir, si no hay lugar suficiente abajo, abrimos HACIA ARRIBA — así el
  // dropdown no se pasa del viewport ni estira la página (evita el scroll + el
  // hueco en blanco). Se mide el espacio bajo el campo en ese momento.
  const toggleOpen = () => {
    if (!open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setOpenUp(window.innerHeight - rect.bottom < 260);
    }
    setOpen((o) => !o);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={toggleOpen}
        className="w-full flex items-center justify-between px-3 py-2.5 border border-neutral-200 rounded-md text-[15px] bg-surface-card hover:bg-surface-elevated focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors"
      >
        <span className="text-brand-black/90">{value}</span>
        <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className={`absolute z-50 w-full bg-surface-card border border-neutral-200 rounded-md shadow-md py-1 max-h-60 overflow-y-auto ${
            openUp ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
        >
          {rubroOptions.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => {
                onChange(r);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-[15px] transition-colors flex items-center justify-between ${
                value === r ? 'bg-neutral-100 text-brand-black/90 font-medium' : 'text-neutral-600 hover:bg-neutral-100'
              }`}
            >
              <span>{r}</span>
              {value === r && <Check className="w-4 h-4 text-brand-primary shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
